import type { ReactNode } from 'react';
import {
  APPLICATION_STATUS_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_STATUS_LABELS,
  type ApplicationStatus,
  type TicketPriority,
  type TicketStatus,
} from '@electrodavisan/shared';
import { cn } from '../../lib/cn';

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-secondary text-text-secondary border-border',
  primary: 'bg-primary-soft text-primary-bright border-primary/30',
  success: 'bg-success-soft text-success border-success/30',
  warning: 'bg-warning-soft text-warning border-warning/30',
  danger: 'bg-danger-soft text-danger border-danger/30',
  info: 'bg-info-soft text-info border-info/30',
};

export interface BadgeProps {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
  dot?: boolean;
}

export function Badge({ tone = 'neutral', children, className, dot }: BadgeProps): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5',
        'text-xs font-medium',
        TONES[tone],
        className,
      )}
    >
      {dot ? <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden /> : null}
      {children}
    </span>
  );
}

/* Mapas de tono por estado. Las etiquetas traducidas viven en el paquete
   compartido para que backend y frontend hablen el mismo idioma. */

const TICKET_STATUS_TONES: Record<TicketStatus, BadgeTone> = {
  OPEN: 'info',
  IN_REVIEW: 'info',
  IN_PROGRESS: 'primary',
  WAITING_USER: 'warning',
  RESOLVED: 'success',
  CLOSED: 'neutral',
  REOPENED: 'warning',
};

export function TicketStatusBadge({ status }: { status: TicketStatus }): JSX.Element {
  return (
    <Badge tone={TICKET_STATUS_TONES[status]} dot>
      {TICKET_STATUS_LABELS[status]}
    </Badge>
  );
}

const TICKET_PRIORITY_TONES: Record<TicketPriority, BadgeTone> = {
  LOW: 'neutral',
  NORMAL: 'info',
  HIGH: 'warning',
  CRITICAL: 'danger',
};

export function TicketPriorityBadge({ priority }: { priority: TicketPriority }): JSX.Element {
  return <Badge tone={TICKET_PRIORITY_TONES[priority]}>{TICKET_PRIORITY_LABELS[priority]}</Badge>;
}

const APPLICATION_STATUS_TONES: Record<ApplicationStatus, BadgeTone> = {
  ACTIVE: 'success',
  MAINTENANCE: 'warning',
  INACTIVE: 'neutral',
  COMING_SOON: 'info',
};

export function ApplicationStatusBadge({ status }: { status: ApplicationStatus }): JSX.Element {
  return (
    <Badge tone={APPLICATION_STATUS_TONES[status]} dot>
      {APPLICATION_STATUS_LABELS[status]}
    </Badge>
  );
}
